import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import API from "../services/api";
import SeoHead from "../components/seo/SeoHead";
import Footer from "../components/Footer";

const THEME_KEY = "hookora_theme";

function ResetPasswordPage() {
  const navigate = useNavigate();

  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const token = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("token") || "";
  }, []);

  const [newPassword, setNewPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState(getStoredTheme());
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  const [showPassword, setShowPassword] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [activeField, setActiveField] = useState("");

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      pageBg: isDark ? "#020617" : "#f8fafc",
      pageGradient: isDark
        ? "radial-gradient(circle at top left, rgba(59,130,246,0.12), transparent 28%), radial-gradient(circle at top right, rgba(124,58,237,0.10), transparent 24%)"
        : "radial-gradient(circle at top left, rgba(59,130,246,0.08), transparent 26%), radial-gradient(circle at top right, rgba(124,58,237,0.08), transparent 22%)",
      cardBg: isDark ? "rgba(15,23,42,0.72)" : "rgba(255,255,255,0.78)",
      border: isDark ? "rgba(148,163,184,0.16)" : "rgba(229,231,235,0.9)",
      borderStrong: isDark ? "rgba(148,163,184,0.28)" : "#d1d5db",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      textSoft: isDark ? "#cbd5e1" : "#374151",
      inputBg: isDark ? "rgba(15,23,42,0.9)" : "rgba(255,255,255,0.95)",
      inputText: isDark ? "#f8fafc" : "#111827",
      eyeButtonBg: isDark ? "#1e293b" : "#f3f4f6",
      eyeButtonText: isDark ? "#e2e8f0" : "#374151",
      link: isDark ? "#93c5fd" : "#2563eb",
      errorBg: isDark ? "rgba(127,29,29,0.25)" : "#fef2f2",
      errorBorder: isDark ? "rgba(248,113,113,0.35)" : "#fecaca",
      errorText: isDark ? "#fecaca" : "#b91c1c",
      successBg: isDark ? "rgba(20,83,45,0.30)" : "#f0fdf4",
      successBorder: isDark ? "rgba(74,222,128,0.30)" : "#bbf7d0",
      successText: isDark ? "#bbf7d0" : "#166534",
      hintBg: isDark ? "rgba(30,41,59,0.72)" : "#f8fafc",
      hintBorder: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      hintGood: isDark ? "#86efac" : "#15803d",
      hintMuted: isDark ? "#94a3b8" : "#6b7280",
      glowBlue: isDark ? "rgba(59,130,246,0.24)" : "rgba(59,130,246,0.18)",
      glowPurple: isDark ? "rgba(124,58,237,0.22)" : "rgba(124,58,237,0.16)",
      glowCyan: isDark ? "rgba(34,211,238,0.16)" : "rgba(34,211,238,0.12)",
      shadow: isDark
        ? "0 22px 60px rgba(2, 6, 23, 0.58)"
        : "0 20px 60px rgba(15, 23, 42, 0.12)",
      cardGlow: isDark
        ? "0 0 0 1px rgba(255,255,255,0.02), 0 24px 80px rgba(2,6,23,0.55)"
        : "0 0 0 1px rgba(255,255,255,0.6), 0 24px 70px rgba(15,23,42,0.10)",
      inputFocusShadow: isDark
        ? "0 0 0 4px rgba(59,130,246,0.12)"
        : "0 0 0 4px rgba(59,130,246,0.10)",
      buttonShadow: loading
        ? "none"
        : isDark
        ? "0 16px 30px rgba(37,99,235,0.24)"
        : "0 14px 26px rgba(37,99,235,0.22)",
    }),
    [isDark, loading]
  );

  const passwordChecks = useMemo(() => {
    const password = newPassword || "";
    return {
      minLength: password.length >= 6,
      hasLetter: /[A-Za-z]/.test(password),
      hasNumberOrSymbol: /[\d\W_]/.test(password),
    };
  }, [newPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      setError("Missing reset token.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setMessage("");

      const response = await API.post("/users/reset-password", {
        token,
        new_password: newPassword
      });

      setMessage(response.data.message);
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (fieldName) => ({
    width: "100%",
    padding: "14px",
    borderRadius: "14px",
    border: `1px solid ${
      activeField === fieldName ? "#3b82f6" : colors.borderStrong
    }`,
    boxSizing: "border-box",
    fontSize: "15px",
    background: colors.inputBg,
    color: colors.inputText,
    outline: "none",
    transition: "all 180ms ease",
    boxShadow:
      activeField === fieldName ? colors.inputFocusShadow : "none",
    transform: activeField === fieldName ? "translateY(-1px)" : "translateY(0)",
  });

  const staggerStyle = (delay) => ({
    opacity: mounted ? 1 : 0,
    transform: mounted ? "translateY(0px)" : "translateY(16px)",
    transition: `opacity 500ms ease ${delay}ms, transform 500ms ease ${delay}ms`,
  });

  const renderCheck = (passed, label) => (
    <div
      style={{
        color: passed ? colors.hintGood : colors.hintMuted,
        fontSize: "13px",
        lineHeight: "1.5",
        display: "flex",
        alignItems: "center",
        gap: "8px",
      }}
    >
      <span style={{ fontWeight: "700" }}>{passed ? "✓" : "•"}</span>
      <span>{label}</span>
    </div>
  );

  return (
    <div
      style={{
        background: colors.pageBg,
        backgroundImage: colors.pageGradient,
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden"
      }}
    >
      <style>
        {`
          @keyframes hookoraFloatOne {
            0% { transform: translate3d(0, 0, 0) scale(1); }
            50% { transform: translate3d(20px, -18px, 0) scale(1.06); }
            100% { transform: translate3d(0, 0, 0) scale(1); }
          }

          @keyframes hookoraFloatTwo {
            0% { transform: translate3d(0, 0, 0) scale(1); }
            50% { transform: translate3d(-24px, 16px, 0) scale(1.04); }
            100% { transform: translate3d(0, 0, 0) scale(1); }
          }

          @keyframes hookoraFloatThree {
            0% { transform: translate3d(0, 0, 0) scale(1); }
            50% { transform: translate3d(12px, 22px, 0) scale(1.08); }
            100% { transform: translate3d(0, 0, 0) scale(1); }
          }

          @keyframes hookoraPulseGlow {
            0% { opacity: 0.72; }
            50% { opacity: 1; }
            100% { opacity: 0.72; }
          }

          @keyframes hookoraButtonShimmer {
            0% { transform: translateX(-120%); }
            100% { transform: translateX(220%); }
          }
        `}
      </style>

      <SeoHead
        title="Reset Password | Hookora"
        description="Reset your Hookora account password."
        path="/reset-password"
      />

      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-50px",
            left: "-40px",
            width: isMobile ? "220px" : "320px",
            height: isMobile ? "220px" : "320px",
            background: colors.glowBlue,
            filter: "blur(60px)",
            borderRadius: "999px",
            animation: "hookoraFloatOne 10s ease-in-out infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "18%",
            right: "-70px",
            width: isMobile ? "220px" : "340px",
            height: isMobile ? "220px" : "340px",
            background: colors.glowPurple,
            filter: "blur(70px)",
            borderRadius: "999px",
            animation: "hookoraFloatTwo 12s ease-in-out infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-90px",
            left: "22%",
            width: isMobile ? "220px" : "300px",
            height: isMobile ? "220px" : "300px",
            background: colors.glowCyan,
            filter: "blur(70px)",
            borderRadius: "999px",
            animation: "hookoraFloatThree 11s ease-in-out infinite",
          }}
        />
      </div>

      <Navbar />

      <div
        style={{
          maxWidth: "520px",
          margin: isMobile ? "24px auto" : "40px auto",
          padding: isMobile ? "14px" : "20px",
          position: "relative",
          zIndex: 2,
        }}
      >
        <div
          style={{
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: isMobile ? "20px" : "24px",
            padding: isMobile ? "22px" : "30px",
            boxShadow: `${colors.shadow}, ${colors.cardGlow}`,
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            opacity: mounted ? 1 : 0,
            transform: mounted
              ? "translateY(0px) scale(1)"
              : "translateY(18px) scale(0.985)",
            transition: "opacity 650ms ease, transform 650ms ease",
          }}
        >
          <div style={staggerStyle(60)}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 12px",
                borderRadius: "999px",
                background: isDark ? "rgba(124,58,237,0.16)" : "rgba(124,58,237,0.10)",
                color: isDark ? "#d8b4fe" : "#7c3aed",
                fontSize: "12px",
                fontWeight: "700",
                marginBottom: "14px",
                animation: "hookoraPulseGlow 3.4s ease-in-out infinite",
              }}
            >
              🔐 Secure reset
            </div>

            <h1
              style={{
                marginTop: 0,
                color: colors.text,
                fontSize: isMobile ? "30px" : "34px",
                lineHeight: "1.08",
                marginBottom: "10px",
              }}
            >
              Reset password
            </h1>

            <p style={{ color: colors.textMuted, marginBottom: "20px", lineHeight: "1.7" }}>
              Enter your new password below and get back into Hookora.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div
              style={{
                ...staggerStyle(180),
                display: "flex",
                gap: "8px",
                alignItems: "center",
              }}
            >
              <input
                type={showPassword ? "text" : "password"}
                placeholder="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onFocus={() => setActiveField("password")}
                onBlur={() => setActiveField("")}
                style={{ ...inputStyle("password"), flex: 1 }}
              />

              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                style={{
                  minWidth: "54px",
                  height: "52px",
                  borderRadius: "14px",
                  border: `1px solid ${colors.borderStrong}`,
                  background: colors.eyeButtonBg,
                  color: colors.eyeButtonText,
                  cursor: "pointer",
                  fontSize: "18px",
                  fontWeight: "700",
                  transition: "transform 160ms ease",
                  transform: showPassword ? "scale(1.04)" : "scale(1)",
                }}
                aria-label={showPassword ? "Hide password" : "Show password"}
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "🙈" : "👁"}
              </button>
            </div>

            <div
              style={{
                ...staggerStyle(240),
                marginTop: "10px",
                background: colors.hintBg,
                border: `1px solid ${colors.hintBorder}`,
                borderRadius: "12px",
                padding: "12px",
                display: "grid",
                gap: "6px",
              }}
            >
              <div
                style={{
                  color: colors.textSoft,
                  fontSize: "13px",
                  fontWeight: "700",
                  marginBottom: "2px",
                }}
              >
                Password should include:
              </div>
              {renderCheck(passwordChecks.minLength, "At least 6 characters")}
              {renderCheck(passwordChecks.hasLetter, "At least 1 letter")}
              {renderCheck(passwordChecks.hasNumberOrSymbol, "At least 1 number or symbol")}
            </div>

            <div style={staggerStyle(300)}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%",
                  marginTop: "14px",
                  padding: "14px",
                  background: loading
                    ? "#9ca3af"
                    : "linear-gradient(135deg, #2563eb, #7c3aed)",
                  color: "white",
                  border: "none",
                  borderRadius: "14px",
                  cursor: loading ? "not-allowed" : "pointer",
                  fontWeight: "700",
                  fontSize: "15px",
                  boxShadow: colors.buttonShadow,
                  position: "relative",
                  overflow: "hidden",
                  transition: "transform 180ms ease, box-shadow 180ms ease, opacity 180ms ease",
                }}
              >
                {!loading && (
                  <span
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "40%",
                      height: "100%",
                      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent)",
                      transform: "translateX(-120%)",
                      animation: "hookoraButtonShimmer 3.6s linear infinite",
                    }}
                  />
                )}
                <span style={{ position: "relative", zIndex: 1 }}>
                  {loading ? "Resetting..." : "Reset password"}
                </span>
              </button>
            </div>
          </form>

          {message && (
            <div
              style={{
                ...staggerStyle(360),
                marginTop: "16px",
                color: colors.successText,
                background: colors.successBg,
                padding: "12px",
                borderRadius: "12px",
                border: `1px solid ${colors.successBorder}`,
                lineHeight: "1.5"
              }}
            >
              {message}
            </div>
          )}

          {error && (
            <div
              style={{
                ...staggerStyle(360),
                marginTop: "16px",
                color: colors.errorText,
                background: colors.errorBg,
                padding: "12px",
                borderRadius: "12px",
                border: `1px solid ${colors.errorBorder}`,
                lineHeight: "1.5"
              }}
            >
              {error}
            </div>
          )}

          <p
            style={{
              ...staggerStyle(420),
              marginTop: "20px",
              color: colors.textMuted
            }}
          >
            Back to{" "}
            <Link to="/login" style={{ color: colors.link, fontWeight: "700", textDecoration: "none" }}>
              Login
            </Link>
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default ResetPasswordPage;